import type { CommentView, GroupSuggestionView, TopicView } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { CommentAuthor } from './CommentCard.js';
import { TopicChipList } from './TopicChips.js';

/**
 * An AI-proposed grouping, awaiting a human decision.
 *
 * Suggestions are advisory only — nothing moves until someone accepts, and
 * individual cards can be pulled out first, because a model that is right four
 * times out of five still needs an easy way to be corrected.
 */
export function SuggestionCard({
  suggestion,
  comments,
  topics,
}: {
  suggestion: GroupSuggestionView;
  comments: CommentView[];
  topics: TopicView[];
}) {
  const send = useRetroStore((state) => state.send);

  const members = suggestion.commentIds
    .map((id) => comments.find((comment) => comment.id === id))
    .filter((comment): comment is CommentView => Boolean(comment));

  if (members.length < 2) return null;

  return (
    <div className="rounded-2xl border-2 border-dashed border-brand-500/50 bg-brand-50/60 p-2">
      <div className="mb-2 flex items-center gap-2 px-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-brand-700">
          Suggested group
        </span>
        {suggestion.title ? (
          <span className="truncate text-xs text-ink/60">“{suggestion.title}”</span>
        ) : null}

        <span className="ml-auto flex gap-1">
          <button
            type="button"
            onClick={() => void send('suggestion:resolve', { suggestionId: suggestion.id, accept: false })}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs text-ink/60 shadow-card hover:text-rose-600"
            aria-label="Reject this suggestion"
            title="Reject"
          >
            ✕
          </button>
          <button
            type="button"
            onClick={() => void send('suggestion:resolve', { suggestionId: suggestion.id, accept: true })}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-xs text-white shadow-card hover:bg-emerald-700"
            aria-label="Accept this suggestion"
            title="Create this group"
          >
            ✓
          </button>
        </span>
      </div>

      <ul className="space-y-1.5">
        {members.map((comment) => (
          <li key={comment.id} className="group relative rounded-xl bg-white p-2.5 shadow-card">
            <p className="whitespace-pre-wrap break-words pr-5 text-sm">{comment.text}</p>
            {comment.topicIds.length > 0 ? (
              <div className="mt-1.5">
                <TopicChipList topicIds={comment.topicIds} topics={topics} />
              </div>
            ) : null}
            <div className="mt-1.5">
              <CommentAuthor comment={comment} />
            </div>
            <button
              type="button"
              onClick={() =>
                void send('suggestion:dropComment', {
                  suggestionId: suggestion.id,
                  commentId: comment.id,
                })
              }
              className="absolute right-1.5 top-1.5 rounded-full p-1 text-xs text-ink/40 opacity-0 transition hover:bg-black/5 hover:text-ink group-hover:opacity-100 focus:opacity-100"
              aria-label="Remove this comment from the suggestion"
              title="Not part of this group"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
